import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from './atoms/Input';
import UserDTO from '../models/user';
import Select from './atoms/Select/Select';
import Button from './atoms/Button';
import {
  useAddUserToEventMutation,
  useUpdateUserEventMutation,
  useGetSellerGroupsQuery,
} from '../store';
import { toastSuccess } from '../utils/toasts';
import Switch from './atoms/Switch';
import CustomAutocomplete from './atoms/Autocomplete';

interface Props {
  businessId: string;
  eventId: string;
  users?: UserDTO[];
  onSuccess: () => void;
  sellerToEdit?: {
    userId: string;
    comissionRate: number;
    active: boolean;
    email: string;
    alias?: string;
    tag?: string;
    tags?: string[];
    seller_group_id?: string;
  };
  onClose?: () => void;
}

interface FormData {
  user_id: string;
  username?: string;
  comission_rate: number;
  active: boolean;
  alias?: string;
  tag?: string;
  seller_group_id?: string;
}

const validationSchema = yup.object<FormData>().shape({
  user_id: yup.string().required('Debes seleccionar un usuario'),
  username: yup.string().optional(),
  active: yup.boolean().required('Debes seleccionar una opción'),
  comission_rate: yup.number()
    .required('Debes ingresar una comisión')
    .typeError('Debes ingresar una comisión válida')
    .min(0, 'La comisión debe ser mayor a 0')
    .max(100, 'La comisión debe ser menor a 100'),
  alias: yup.string().optional(),
  tag: yup.string().optional(),
  seller_group_id: yup.string().optional(),
});

const AddEditSellerToEvent: React.FC<Props> = ({
  users, businessId, eventId, onSuccess, sellerToEdit, onClose,
}) => {
  const { data: sellerGroups } = useGetSellerGroupsQuery({ businessId: businessId || '' }, { skip: !businessId });
  const navigate = useNavigate();
  const form = useForm<FormData>({
    resolver: yupResolver<FormData>(validationSchema),
    defaultValues: sellerToEdit ? {
      user_id: sellerToEdit.userId,
      comission_rate: sellerToEdit.comissionRate * 100,
      active: sellerToEdit.active,
      alias: sellerToEdit.alias,
      tag: sellerToEdit.tag,
      seller_group_id: sellerToEdit.seller_group_id,
    } : {
      user_id: '',
      active: true,
    },
  });
  const {
    control, handleSubmit, formState, watch, setValue,
  } = form;
  const [addUserToEvent, addUserToEventResults] = useAddUserToEventMutation();
  const [editUserInEvent, editUserInEventResults] = useUpdateUserEventMutation();
  let res;

  const username = watch('username');

  useEffect(() => {
    if (!users) { return; }
    const selectedUser = users.find((u) => u.username === username);
    setValue('user_id', selectedUser?.id || '');
  }, [username, setValue, users]);

  const handleAddUser = async () => {
    try {
      const values = form.getValues();
      console.log('values', values);
      if (sellerToEdit) {
        res = await editUserInEvent({
          businessId,
          eventId,
          data: {
            id: sellerToEdit.userId,
            seller_comission_rate: values.comission_rate / 100,
            active: values.active,
            alias: values.alias,
            tag: values.tag,
            seller_group_id: values.seller_group_id,
          },

        });
      } else {
        const selectedUser = users?.find((user) => user.id === values.user_id);
        const userBusinessPermissions = selectedUser?.user_businesses?.find(
          (ub) => ub.business_id === businessId,
        )?.permissions || 'SELLER';

        res = await addUserToEvent({
          businessId,
          eventId,
          userData: {
            userId: values.user_id,
            comissionRate: values.comission_rate / 100,
            active: values.active,
            permissions: userBusinessPermissions,
            seller_group_id: values.seller_group_id,
          },
        });
      }
      if (!('error' in res)) {
        toastSuccess(sellerToEdit ? 'Vendedor actualizado' : 'Vendedor agregado al evento');
        onSuccess();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form className="w-full h-full flex flex-col gap-2" onSubmit={handleSubmit(handleAddUser)}>
      <Switch
        label="Habilitado para vender"
        labelClassName="font-medium"
        checked={watch('active')}
        onChange={(e) => {
          setValue('active', e.target.checked);
        }}
      />
      {sellerToEdit && <Input
        label="Correo electrónico *"
        labelAbove
        id="email"
        value={sellerToEdit?.email}
        placeholder="Ingresa el correo electrónico"
        disabled
      />}
      {sellerToEdit && <Controller
        name="alias"
        control={control}
        render={({ field }) => (
          <Input
            label="Alias (opcional)"
            labelAbove
            id="alias"
            placeholder="Ingresa el alias"
            {...field}
            errMsg={formState.errors.alias?.message}
          />
        )}
      />}
      {!sellerToEdit && (
      <>
        <CustomAutocomplete
          id="username"
          name="username"
          label="Usuario *"
          control={control}
          options={users?.map((u) => u.username).sort() || []}
          freeSolo={false}
          labelAbove
          noMatchesText="No se encontró el usuario, créalo primero"
          noMatches={() => {
            navigate(`/admin/${businessId}/users`);
          }}
        />
        {formState.errors.user_id
            && <p className="text-sm text-red-500">{formState.errors.user_id.message}</p>}
      </>
      )}
      <Controller
        name="comission_rate"
        control={control}
        render={({ field }) => (
          <Input
            label="Comisión *"
            labelAbove={true}
            id="comission_rate"
            type="number"
            {...field}
            errMsg={formState.errors.comission_rate?.message}
          />
        )}
      />
      {sellerToEdit && (
        <CustomAutocomplete
          id="tag"
          name="tag"
          label="Etiqueta (opcional)"
          control={control}
          options={sellerToEdit.tags?.filter((tag) => tag && typeof tag === 'string').sort() || []}
          labelAbove
        />
      )}
      {(sellerGroups && sellerGroups.length > 0) && (
        <Controller
          name="seller_group_id"
          control={control}
          render={({ field }) => (
            <Select
              label="Grupo de vendedores"
              id="seller_group_id"
              options={sellerGroups.map((group) => ({
                label: group.name,
                value: group.id,
              }))}
              {...field}
            />
          )}
        />
      )}
      <div className="flex mt-4 gap-2">
        <Button
          variant="secondary"
          onClick={onClose}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={addUserToEventResults.isLoading || editUserInEventResults?.isLoading}
        >
          Guardar
        </Button>
      </div>
    </form>
  );
};

export default AddEditSellerToEvent;
